/**
 * @module Date
 */

import { Injectable } from '@angular/core';
import { DateClass } from '@class/date.class';
import { Observable, BehaviorSubject } from 'rxjs';
import { DateModel } from '@models/date.model';
import { map } from 'rxjs/operators';
import { FirestoreService } from '@services/firestore/firestore.service';
import { AuthService } from '@services/auth/auth.service';
import { FirebaseService } from '@services/firebase/firebase.service';
import { UidService } from '@services/uid/uid.service';
import { ItemType } from '@models/item.model';
import { LinksModelConstructor } from '@functions/link.functions';
import { DeepMerge } from '@functions/copy.functions';

@Injectable({
  providedIn: 'root'
})
export class DateService extends FirestoreService<DateModel> {

  /**
   * @description type de document manipulé par le service
   * @protected
   * @type {ResourceType}
   * @memberof DateService
   */
  protected _itemType: ItemType = 'dates';

  /**
   * @description true si l'accès à ce type de document peut nécessiter des habilitations
   * @protected
   * @type {boolean}
   * @memberof DateService
   */
  protected _restricted: boolean = false;

  /**
   * @description Creates an instance of DateService
   * @param {UidService} $uid
   * @param {AuthService} $auth
   * @param {FirebaseService} $firebase
   * @memberof DateService
   */
  constructor(
    private $uid: UidService,
    protected $auth: AuthService,
    protected $firebase: FirebaseService
  ) {
    super($firebase, $auth);
  }

  /**
   * @description Observateur de la liste des objects autorisés pour l'utilisateur courant
   * @returns {BehaviorSubject<DateClass[]>}
   * @memberof DateService
   */
  list$(): BehaviorSubject<DateClass[]> {
    return super.full$().pipe(
      map((dates: DateModel[]) => {
        if(!!dates) {
          let ds = dates.map<DateClass>((date: DateModel) => {
            return new DateClass(date, true);
          });
          return ds;
        }
      })
    ) as BehaviorSubject<DateClass[]>;
  }

  /**
   * @description Observateur d'un document dont l'uid est passé en paramètre
   * @param {string} uid
   * @returns {Observable<DateClass>}
   * @memberof DateService
   */
  get$(uid: string): Observable<DateClass> {
    return super.doc$(uid).pipe(
      map((date: DateModel) => {
        return new DateClass(date, true);
      })
    );
  }

  /**
   * @description Observateur de la liste des objects (données partielles) autorisés pour l'utilisateur courant
   * @returns {Observable<DateClass[]>}
   * @memberof DateService
   */
  listPartials$(): Observable<DateClass[]> {
    return super.fullPartial$().pipe(
      map((dates: DateModel[]) => {
        if(!!dates) {
          let ds = dates.map<DateClass>((date: DateModel) => {
            return new DateClass(date, true);
          });
          return ds;
        }
      })
    );
  }

  /**
   * @description Observateur d'un document (données partielles) dont l'uid est passé en paramètre
   * @param {string} uid
   * @returns {Observable<DateClass>}
   * @memberof DateService
   */
  getPartial$(uid: string): Observable<DateClass> {
    return super.partial$(uid).pipe(
      map((date: DateModel) => {
        if(date !== null) return new DateClass(date, true);
      })
    );
  }

  /**
   * @description Créé un nouveau document avec les valeurs par défaut
   * @param {Partial<DateModel>} [model={}]
   * @returns {DateClass}
   * @memberof DateService
   */
  create(model: Partial<DateModel> = {}): DateClass {
    let _model = { 
      uid: this.$uid.create(),
      links: LinksModelConstructor().links,
      logs: {},
      tags: {},
      linkedColumns: [],
      labels: undefined,
      descriptions: undefined,
      frequency: 'M',
      dateFormats: []
    } as DateModel;

    _model = DeepMerge(_model, model as DateModel);

    return new DateClass(_model, false);
  }

  /**
   * @description Créé ou met à jour le document passé en paramètre
   * @param {DateClass} dashboard
   * @returns {Promise<void>}
   * @memberof DateService
   */
  async set(date: DateClass): Promise<void> {
    return super.update(date.uid.value, date.model);
  }

  /**
   * @description Supprime le document passé en paramètre
   * @param {string} uid
   * @returns {Promise<void>}
   * @memberof DateService
   */
  async remove(uid: string): Promise<void> {
    return super.delete(uid);
  }

}
